import DownloadCSVMenuButton, {
    downloadTypes,
} from 'components/Button/DownloadCSVMenuButton';
import { downloadItemsAsCSV, downloadTimeSeriesAsCSV } from 'utils/formatCSV';
import { DataTable } from '../DataTable';
import DrawerContent from '../../DrawerContent';
import { GOOGLE_METADATA_BY_KEY } from 'utils/metrics';
import SectionTableFilterChips from '../../SectionTableFilterChips';
import { TIKTOK_COUNTRIES } from '@/config/constants';
import TableHeading from '../../TableHeading';
import toast from 'react-hot-toast';
import { useCurrentCountry } from 'utils/hooks';
import useGoogleRelatedEntities from 'utils/hooks/useGoogleRelatedEntities';
import useGoogleRelatedTableProps from './useGoogleRelatedTableProps.hook';

const dimensionsToDrawerProps = {
    affinities: 'affinityId',
    brands: 'brandId',
    categories: 'categoryId',
    products: 'productId',
    countries: 'countryId',
};

function GoogleRelatedSectionTable({
    icon,
    title,
    subtitle,
    tooltipKey,
    tooltipExample,
    categories,
    brands,
    countries,
    paginateBy,
    name,
    filename = 'trend table',
    controller,
    getColumnsConfig,
    sortParamKey,
    handleFilter,
    tools,
    initialSortOption = GOOGLE_METADATA_BY_KEY.volume.getSortOption({
        ascending: false,
    }),
    initialFilterOption = {},
    filterParamSeparator,
    filterParamPrefix,
    filtersSlot,
    showParentNameWhenMultiple = false,
    ...restProps
}) {
    const country = useCurrentCountry();
    const normalizedCategories = categories
        ? Array.isArray(categories)
            ? categories
            : [categories]
        : null;
    const normalizedBrands = brands
        ? Array.isArray(brands)
            ? brands
            : [brands]
        : null;
    const normalizedCountries = countries
        ? Array.isArray(countries)
            ? countries
            : [countries]
        : null;
    const { query: countQuery } = useGoogleRelatedEntities({
        categories: normalizedCategories,
        brands: normalizedBrands,
        countries: normalizedCountries,
        paginateBy,
        per_page: 1,
        // The count exists only for the heading; without one this is a request
        // per page load for a number nothing renders.
        enabled: !!(title && (categories || brands) && countries),
    });
    const totalRecords = countQuery.isPreviousData
        ? null
        : (countQuery.data?.pages?.[0]?.total_records ?? null);
    const {
        columnsMenu,
        dataReady,
        isLoading,
        noDataMessage,
        rows,
        sortedData,
        filters,
        filtersKey,
        setFilter,
        setFilters,
        sortOption,
        setSortOption,
        sortOptionKey,
        visibleColumnsConfig,
        handleSearchInputChange,
        queryData,
        fetchNextPage,
        isFetchingNextPage,
        isFetching,
        getQueryData,
    } = useGoogleRelatedTableProps({
        categories: normalizedCategories,
        brands: normalizedBrands,
        countries: normalizedCountries,
        paginateBy,
        name,
        getColumnsConfig,
        handleFilter,
        initialSortOption,
        initialFilterOption,
        sortParamKey,
        filterParamSeparator,
        filterParamPrefix,
    });

    const isNotTikTokMarket =
        country && !TIKTOK_COUNTRIES.includes(country.text_id);

    // With one group selected every row repeats it, so the column says nothing.
    const showParentNameColumn =
        !showParentNameWhenMultiple || (filters?.parentName?.length || 0) > 1;
    const columnsForDisplay = showParentNameColumn
        ? visibleColumnsConfig
        : visibleColumnsConfig.filter((column) => column.key !== 'parentName');

    const resolvedFiltersSlot =
        typeof filtersSlot === 'function'
            ? filtersSlot({
                  handleFilter,
                  filters,
                  setFilter,
                  setFilters,
                  queryData,
              })
            : filtersSlot;

    const csvMenu = (
        <DownloadCSVMenuButton
            isDisabled={!dataReady}
            isLoading={isLoading}
            options={[
                {
                    mixpanelMeta: {
                        label: 'card data csv',
                        contentKey: 'DataTable',
                    },
                    children: 'Table Data CSV',
                    onClick: () => {
                        toast.promise(
                            (async () => {
                                const allData = await getQueryData();
                                downloadItemsAsCSV(filename, allData, [
                                    ...columnsForDisplay,
                                    { key: 'cluster' },
                                ]);
                            })(),
                            {
                                loading: 'Preparing download...',
                                success: 'Download complete!',
                                error: 'Error downloading CSV',
                            },
                        );
                    },
                    downloadType: downloadTypes.CSV,
                    key: 'card data csv',
                },
                {
                    mixpanelMeta: {
                        label: 'time series csv',
                        contentKey: 'DataTable',
                    },
                    children: 'Time Series CSV',
                    onClick: () => {
                        toast.promise(
                            (async () => {
                                const allData = await getQueryData();
                                downloadTimeSeriesAsCSV(
                                    `${filename} time series data`,
                                    allData,
                                    {
                                        skipPrediction: !isNotTikTokMarket,
                                        ...(paginateBy === 'affinities' && {
                                            entityColumns: showParentNameColumn
                                                ? [
                                                      {
                                                          key: 'name',
                                                      },
                                                      {
                                                          key: 'parentName',
                                                          label: 'group',
                                                      },
                                                  ]
                                                : [
                                                      {
                                                          key: 'name',
                                                      },
                                                  ],
                                        }),
                                    },
                                );
                            })(),
                            {
                                loading: 'Preparing download...',
                                success: 'Download complete!',
                                error: 'Error downloading CSV',
                            },
                        );
                    },
                    downloadType: downloadTypes.CSV,
                    key: 'time series csv',
                },
            ]}
            mixpanelMeta={{ contentKey: 'DataTable' }}
        />
    );

    return (
        <>
            {title && (
                <TableHeading
                    icon={icon}
                    title={title}
                    subtitle={subtitle}
                    count={totalRecords}
                    tooltipKey={tooltipKey}
                    tooltipExample={tooltipExample}
                    tools={
                        <>
                            {tools}
                            {csvMenu}
                        </>
                    }
                />
            )}
            {/* The heading strip is what insets this card's body. With no
                heading the surrounding SectionCard already pads, and adding
                p-5 on top of it doubled the gutter on the explore page. */}
            <div
                className={
                    title ? 'flex flex-col gap-5 p-5' : 'flex flex-col gap-5'
                }
            >
                <div className="flex flex-nowrap gap-3 items-start">
                    <div className="grow">
                        {resolvedFiltersSlot || (
                            <SectionTableFilterChips
                                queryData={queryData}
                                handleFilter={handleFilter}
                                filters={filters}
                                setFilter={setFilter}
                                setFilters={setFilters}
                            />
                        )}
                    </div>
                    {/* With no heading to hold them, the download and column
                        controls sit together above the table. */}
                    {!title && csvMenu}
                    {columnsMenu}
                </div>
                <DataTable
                    isLoading={isLoading}
                    isFetching={isFetching}
                    fetchNextPage={fetchNextPage}
                    isFetchingNextPage={isFetchingNextPage}
                    data={sortedData}
                    noDataMessage={noDataMessage}
                    renderDrawerContent={(selectedId) => {
                        const drawerProps = {
                            controller,
                        };
                        if (paginateBy === 'affinities') {
                            drawerProps.affinityId = selectedId;
                            drawerProps.paginateBy = 'affinityId';
                            if (categories)
                                drawerProps.categoryId = Array.isArray(
                                    categories,
                                )
                                    ? categories[0]
                                    : categories;
                            if (brands)
                                drawerProps.brandId = Array.isArray(brands)
                                    ? brands[0]
                                    : brands;
                        } else if (paginateBy === 'brands') {
                            drawerProps.brandId = selectedId;
                            drawerProps.paginateBy = 'brandId';
                            if (categories)
                                drawerProps.categoryId = Array.isArray(
                                    categories,
                                )
                                    ? categories[0]
                                    : categories;
                        }
                        if (countries)
                            drawerProps.countryId = Array.isArray(countries)
                                ? countries[0]
                                : countries;
                        return (
                            <DrawerContent
                                {...drawerProps}
                                showAddToWatchlist={
                                    drawerProps.paginateBy === 'affinityId'
                                }
                                skipPrediction={!isNotTikTokMarket}
                                source="google"
                            />
                        );
                    }}
                    sortOption={sortOption}
                    setSortOption={setSortOption}
                    onSearchInputChange={handleSearchInputChange}
                    dataKey={`d-*-s-${sortOptionKey}-f-${filtersKey}`}
                    columns={columnsForDisplay}
                    {...restProps}
                />
            </div>
        </>
    );
}

export default GoogleRelatedSectionTable;
